import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import {
  Payment,
  PaymentFilters,
  PaymentStatus,
  PaymentMethod,
  PaymentType,
  DEFAULT_PAYMENT_FILTERS,
  PAYMENT_METHOD_COLORS,
  calculatePaymentStats,
  formatPaymentMethod,
  getPaymentMethodIcon,
} from '@/types/payments';
import { getPayments } from '@/lib/payments-service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search,
  Eye,
  RefreshCw,
  Download,
  ExternalLink,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/operator/payments/')({
  component: PaymentsPage,
});

// ============================================================================
// Badge Variants
// ============================================================================

function getStatusBadgeVariant(status: PaymentStatus): "paid" | "unpaid" | "pending" | "refunded" | "secondary" {
  const map: Record<PaymentStatus, "paid" | "unpaid" | "pending" | "refunded" | "secondary"> = {
    PENDING: 'pending',
    PROCESSING: 'pending',
    COMPLETED: 'paid',
    FAILED: 'unpaid',
    REFUNDED: 'refunded',
    PARTIALLY_REFUNDED: 'refunded',
    CANCELLED: 'secondary',
    EXPIRED: 'secondary',
  };
  return map[status];
}

// ============================================================================
// Component
// ============================================================================

function PaymentsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // State
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<PaymentFilters>(DEFAULT_PAYMENT_FILTERS);

  // Fetch payments
  useEffect(() => {
    async function fetchPayments() {
      setLoading(true);
      try {
        const data = await getPayments({
          businessId: selectedBusinessId || undefined,
        });
        setPayments(data);
      } catch (error) {
        console.error('Failed to fetch payments:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPayments();
  }, [selectedBusinessId]);

  // Filter payments
  const filteredPayments = useMemo(() => {
    let result = [...payments];

    if (filters.status && filters.status !== 'ALL') {
      result = result.filter((p) => p.status === filters.status);
    }

    if (filters.method && filters.method !== 'ALL') {
      result = result.filter((p) => p.method === filters.method);
    }

    if (filters.type && filters.type !== 'ALL') {
      result = result.filter((p) => p.type === filters.type);
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.customerName.toLowerCase().includes(query) ||
          p.transactionId.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.id.toLowerCase().includes(query)
      );
    }

    return result;
  }, [payments, filters]);

  // Stats
  const stats = useMemo(() => calculatePaymentStats(filteredPayments), [filteredPayments]);

  // Handlers
  const handleFilterChange = (key: keyof PaymentFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleViewPayment = (paymentId: string) => {
    navigate({ to: `/operator/payments/${paymentId}` });
  };

  const handleViewBooking = (bookingId: string) => {
    navigate({ to: `/operator/bookings`, search: { id: bookingId } });
  };

  const handleViewOrder = (orderId: string) => {
    navigate({ to: `/operator/orders`, search: { id: orderId } });
  };

  // Format helpers
  const formatCurrency = (amount: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  const allStatuses: PaymentStatus[] = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'REFUNDED', 'CANCELLED'];
  const allMethods: PaymentMethod[] = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'CASH', 'DIGITAL_WALLET'];
  const allTypes: PaymentType[] = ['SERVICE', 'PRODUCT', 'DEPOSIT', 'TIP', 'REFUND'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('payments.title')}</h1>
          <p className="text-muted-foreground">
            {selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')} · {t('payments.paymentCount', { count: filteredPayments.length })}
          </p>
        </div>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          {t('common.export')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('payments.todaysPayments')}</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.todayAmount)}</div>
            <p className="text-xs text-muted-foreground">
              {stats.todayCount} {t('payments.transactions')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('payments.totalCompleted')}</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {formatCurrency(stats.completedAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.byStatus.COMPLETED || 0} {t('payments.payments')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('payments.pending')}</CardTitle>
            <RefreshCw className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {formatCurrency(stats.pendingAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {(stats.byStatus.PENDING || 0) + (stats.byStatus.PROCESSING || 0)} {t('payments.awaitingPayment')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('payments.refunded')}</CardTitle>
            <ArrowDownRight className="h-4 w-4 text-violet-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-violet-600">
              {formatCurrency(stats.refundedAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {(stats.byStatus.REFUNDED || 0) + (stats.byStatus.PARTIALLY_REFUNDED || 0)} {t('payments.refunds')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('payments.status')}</label>
              <Select
                value={filters.status || 'ALL'}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('payments.allStatuses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('payments.allStatuses')}</SelectItem>
                  {allStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {formatStatus(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('payments.method')}</label>
              <Select
                value={filters.method || 'ALL'}
                onValueChange={(value) => handleFilterChange('method', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('payments.allMethods')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('payments.allMethods')}</SelectItem>
                  {allMethods.map((method) => (
                    <SelectItem key={method} value={method}>
                      {getPaymentMethodIcon(method)} {formatPaymentMethod(method)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('payments.type')}</label>
              <Select
                value={filters.type || 'ALL'}
                onValueChange={(value) => handleFilterChange('type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('payments.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('payments.allTypes')}</SelectItem>
                  {allTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {formatStatus(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-[2] min-w-[250px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('common.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('payments.searchPlaceholder')}
                  value={filters.searchQuery || ''}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      {filteredPayments.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <h3 className="text-lg font-semibold">{t('payments.noPaymentsFound')}</h3>
            <p className="text-sm text-muted-foreground">{t('common.tryAdjustingFilters')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('payments.transaction')}</TableHead>
                <TableHead>{t('payments.customer')}</TableHead>
                <TableHead>{t('payments.amount')}</TableHead>
                <TableHead>{t('payments.method')}</TableHead>
                <TableHead>{t('payments.type')}</TableHead>
                <TableHead>{t('payments.status')}</TableHead>
                <TableHead>{t('payments.linked')}</TableHead>
                <TableHead>{t('payments.date')}</TableHead>
                <TableHead className="text-right">{t('payments.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow
                  key={payment.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleViewPayment(payment.id)}
                >
                  <TableCell>
                    <div className="font-mono text-sm">{payment.transactionId.slice(0, 12)}...</div>
                    <div className="text-xs text-muted-foreground">{payment.id}</div>
                  </TableCell>
                  <TableCell>
                    {payment.customerId ? (
                      <button
                        className="font-medium text-left hover:text-primary hover:underline transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate({ to: `/operator/customers/${payment.customerId}` });
                        }}
                      >
                        {payment.customerName}
                      </button>
                    ) : (
                      <span className="font-medium">{payment.customerName}</span>
                    )}
                    {payment.customerPhone && (
                      <div className="text-sm text-muted-foreground">{payment.customerPhone}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={`font-semibold ${payment.type === 'REFUND' ? 'text-violet-600' : ''}`}>
                      {payment.type === 'REFUND' ? '-' : ''}{formatCurrency(payment.amount, payment.currency)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getPaymentMethodIcon(payment.method)}</span>
                      <span className="text-sm">{formatPaymentMethod(payment.method)}</span>
                    </div>
                    {payment.cardLast4 && (
                      <div className="text-xs text-muted-foreground">
                        {payment.cardBrand} {'\u2022\u2022\u2022\u2022'} {payment.cardLast4}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{formatStatus(payment.type)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(payment.status)}>
                      {formatStatus(payment.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {payment.bookingId && (
                        <button
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewBooking(payment.bookingId!);
                          }}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t('payments.booking')}
                        </button>
                      )}
                      {payment.orderId && (
                        <button
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(payment.orderId!);
                          }}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t('payments.order')}
                        </button>
                      )}
                      {!payment.bookingId && !payment.orderId && (
                        <span className="text-xs text-muted-foreground">{'\u2014'}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{formatDateTime(payment.createdAt)}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewPayment(payment.id);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
